import {
  Briefcase,
  CheckCircle2,
  Clock,
  FileCheck,
  MapPin,
  Shield,
  Star,
  Upload,
  Users,
  Wallet,
  Zap,
} from "lucide-react";
import { type AudienceType } from "@/components/home/PersonaSelector";

/* ------------------------------------------------------------------ */
/*  Colour mode: homepage uses persona accents, persona pages use     */
/*  platform primary so mock-UI looks like the real product.          */
/* ------------------------------------------------------------------ */

type ColorMode = "persona" | "primary";

interface CardStackProps {
  colorMode?: ColorMode;
}

/* helpers */
const accent = (mode: ColorMode, persona: "worker" | "agency" | "employer") =>
  mode === "primary" ? "primary" : persona;

/* ------------------------------------------------------------------ */
/*  TEMPS – 3 cards                                                   */
/* ------------------------------------------------------------------ */

function TempsCards({ colorMode = "persona" }: CardStackProps) {
  const c = accent(colorMode, "worker");

  return (
    <div className="space-y-3">
      {/* Card 1 – Connected Agencies */}
      <div className="bg-card rounded-xl border border-border p-4 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <p className="font-heading font-semibold text-sm">My Agencies</p>
          <span className={`text-xs text-${c} font-medium`}>3 connected</span>
        </div>
        <div className="space-y-2">
          {[
            { name: "Swift Staff", initial: "SS" },
            { name: "ProTemps UK", initial: "PT" },
            { name: "FlexForce", initial: "FF" },
          ].map((a) => (
            <div key={a.name} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full bg-${c}/10 flex items-center justify-center text-xs font-semibold text-${c}`}>
                  {a.initial}
                </div>
                <span className="text-sm">{a.name}</span>
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full bg-${c}/10 text-${c} font-medium`}>Connected</span>
            </div>
          ))}
        </div>
      </div>

      {/* Card 2 – Available Shifts (compact) */}
      <div className="bg-card rounded-xl border border-border p-4 shadow-xl ml-4">
        <div className="flex items-center justify-between mb-3">
          <p className="font-heading font-semibold text-sm">Available Shifts</p>
          <span className="text-xs text-muted-foreground">Near you</span>
        </div>
        <div className="space-y-2">
          {[
            { title: "Event Staff", location: "Manchester Arena", pay: "£14/hr" },
            { title: "Warehouse Pick & Pack", location: "Trafford Park", pay: "£12.50/hr" },
          ].map((s) => (
            <div key={s.title} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{s.title}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3 w-3 shrink-0" />
                  {s.location}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-sm font-semibold text-${c}`}>{s.pay}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full bg-${c}/10 text-${c} font-medium cursor-pointer`}>Accept</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Card 3 – Payment Tracker mini */}
      <div className="bg-card rounded-xl border border-border p-4 shadow-xl ml-8">
        <div className="flex items-center gap-2 mb-2">
          <Wallet className={`h-4 w-4 text-${c}`} />
          <p className="font-heading font-semibold text-sm">This Week</p>
        </div>
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <p className={`text-xl font-heading font-bold text-${c}`}>£487.50</p>
            <p className="text-xs text-muted-foreground">32.5 hrs · 4 shifts</p>
          </div>
          <div className="text-right space-y-0.5 text-xs text-muted-foreground">
            <p>Base: £462.50</p>
            <p>Holiday: £25.00</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  AGENCIES – 3 cards                                                */
/* ------------------------------------------------------------------ */

function AgenciesCards({ colorMode = "persona" }: CardStackProps) {
  const c = accent(colorMode, "agency");

  return (
    <div className="space-y-3">
      {/* Card 1 – Worker Onboarding */}
      <div className="bg-card rounded-xl border border-border p-4 shadow-xl">
        <div className="flex items-center gap-2 mb-3">
          <div className={`p-1.5 rounded-lg bg-${c}/10`}>
            <FileCheck className={`h-4 w-4 text-${c}`} />
          </div>
          <div>
            <p className="font-heading font-semibold text-sm">Worker Onboarding</p>
            <p className="text-xs text-muted-foreground">Sarah Johnson</p>
          </div>
        </div>
        <div className="space-y-1.5">
          {[
            { label: "Photo ID", done: true },
            { label: "Right to Work", done: true },
            { label: "Bank Details", done: true },
            { label: "Emergency Contact", done: false },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2 text-sm">
              <CheckCircle2 className={`h-3.5 w-3.5 ${item.done ? `text-${c}` : "text-muted-foreground/40"}`} />
              <span className={item.done ? "" : "text-muted-foreground"}>{item.label}</span>
            </div>
          ))}
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
          <div className={`h-full rounded-full bg-${c} w-3/4`} />
        </div>
        <p className="text-xs text-muted-foreground mt-1">75% complete</p>
      </div>

      {/* Card 2 – Worker Portal / Self-serve */}
      <div className="bg-card rounded-xl border border-border p-4 shadow-xl ml-4">
        <div className="flex items-center justify-between mb-3">
          <p className="font-heading font-semibold text-sm">Worker Portal</p>
          <span className="text-xs text-muted-foreground">Live view</span>
        </div>
        <div className="space-y-2">
          {[
            { title: "Warehouse Assistant", time: "Mon 7am-3pm", status: "Accepted" },
            { title: "Event Setup", time: "Wed 5pm-11pm", status: "New" },
          ].map((s) => (
            <div key={s.title} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
              <div>
                <p className="text-sm font-medium">{s.title}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {s.time}
                </p>
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                s.status === "Accepted"
                  ? `bg-${c}/10 text-${c}`
                  : "bg-muted text-muted-foreground"
              }`}>
                {s.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Card 3 – Digital Coordination mini */}
      <div className="bg-card rounded-xl border border-border p-4 shadow-xl ml-8">
        <div className="flex items-center gap-2 mb-2">
          <Upload className={`h-4 w-4 text-${c}`} />
          <p className="font-heading font-semibold text-sm">Activity</p>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-xs">
            <span className={`w-1.5 h-1.5 rounded-full bg-${c}`} />
            <span>Right to Work uploaded digitally</span>
            <span className="text-muted-foreground ml-auto">2m ago</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40" />
            <span>Profile completed — no office visit needed</span>
            <span className="text-muted-foreground ml-auto">1h ago</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  EMPLOYERS – 3 cards                                               */
/* ------------------------------------------------------------------ */

function EmployersCards({ colorMode = "persona" }: CardStackProps) {
  const c = accent(colorMode, "employer");

  return (
    <div className="space-y-3">
      {/* Card 1 – Preferred Suppliers */}
      <div className="bg-card rounded-xl border border-border p-4 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <p className="font-heading font-semibold text-sm">Preferred Suppliers</p>
          <span className={`text-xs text-${c} font-medium`}>PSL</span>
        </div>
        <div className="space-y-2">
          {[
            { name: "Swift Staff Agency", status: "Active", workers: 48 },
            { name: "ProTemps UK", status: "Active", workers: 31 },
            { name: "FlexForce Ltd", status: "Pending", workers: 0 },
          ].map((a) => (
            <div key={a.name} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Users className={`h-3.5 w-3.5 ${a.status === "Active" ? `text-${c}` : "text-muted-foreground"}`} />
                <span className="text-sm">{a.name}</span>
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                a.status === "Active"
                  ? `bg-${c}/10 text-${c}`
                  : "bg-muted text-muted-foreground"
              }`}>
                {a.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Card 2 – Shift Applicants */}
      <div className="bg-card rounded-xl border border-border p-4 shadow-xl ml-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="font-heading font-semibold text-sm">Warehouse Assistant</p>
            <p className="text-xs text-muted-foreground">Tomorrow, 06:00 – 14:00</p>
          </div>
          <div className="flex items-center gap-1">
            <Zap className={`h-3.5 w-3.5 text-${c}`} />
            <span className={`text-xs text-${c} font-medium`}>5 applied</span>
          </div>
        </div>
        <div className="space-y-1.5">
          {[
            { name: "James M.", rating: "4.8", match: "97%" },
            { name: "Priya K.", rating: "4.6", match: "94%" },
          ].map((w) => (
            <div key={w.name} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-medium">
                  {w.name.charAt(0)}
                </div>
                <span className="text-sm">{w.name}</span>
                <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                  <Star className="h-3 w-3 text-amber-500" />
                  {w.rating}
                </span>
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full bg-${c}/10 text-${c} font-medium`}>{w.match}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Card 3 – Verified Worker mini */}
      <div className="bg-card rounded-xl border border-border p-4 shadow-xl ml-8">
        <div className="flex items-center gap-2 mb-2">
          <Shield className={`h-4 w-4 text-${c}`} />
          <p className="font-heading font-semibold text-sm">Pre-verified</p>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {["Photo ID", "Right to Work", "DBS Check", "References"].map((doc) => (
            <div key={doc} className="flex items-center gap-1.5 text-xs">
              <CheckCircle2 className={`h-3 w-3 text-${c}`} />
              <span>{doc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  DEFAULT – Platform overview (unchanged)                           */
/* ------------------------------------------------------------------ */

function MarketplaceOverviewPreview() {
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-xl">
      <div className="flex items-center justify-between mb-6">
        <p className="font-heading font-semibold">Platform Overview</p>
        <span className="text-xs text-agency font-medium">Live</span>
      </div>
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { value: "24", label: "Agencies", color: "text-agency" },
          { value: "130+", label: "Employers", color: "text-employer" },
          { value: "85", label: "Open Shifts", color: "text-worker" },
        ].map((stat) => (
          <div key={stat.label} className="text-center p-3 bg-muted/50 rounded-lg">
            <p className={`text-2xl font-heading font-bold ${stat.color}`}>{stat.value}</p>
            <p className="text-xs text-muted-foreground">{stat.label}</p>
          </div>
        ))}
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 bg-agency/5 rounded-lg">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-agency" />
            <span className="text-sm">New agency onboarded</span>
          </div>
          <span className="text-xs text-muted-foreground">5m ago</span>
        </div>
        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-accent" />
            <span className="text-sm">42 shifts filled today</span>
          </div>
          <span className="text-xs text-muted-foreground">1h ago</span>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Exports                                                           */
/* ------------------------------------------------------------------ */

export { TempsCards, AgenciesCards, EmployersCards };

export default function HeroPreviewCards({ audience }: { audience: AudienceType | null }) {
  if (audience === null) return <MarketplaceOverviewPreview />;
  switch (audience) {
    case "temps":
      return <TempsCards colorMode="persona" />;
    case "agencies":
      return <AgenciesCards colorMode="persona" />;
    case "employers":
      return <EmployersCards colorMode="persona" />;
  }
}
